import { useCallback } from 'react';

import {
  VIDEO_PLAYBACK_PROCESSING,
  type VideoPlaybackAuthorization,
  type VideoPlaybackResolver,
} from '@shared/dream-components';

import { useEnsureCsrfToken } from '~/context/CsrfContext';

// Resolves playback for any hosted video by its id. Rails authorizes it through the video's parent, so the same
// resolver serves every surface that renders a video. A 409 means the video is still processing, which the player
// polls on; any other failure resolves to null so the player shows its own fallback rather than throw.
const useVideoPlayback = (): VideoPlaybackResolver => {
  const ensureCsrfToken = useEnsureCsrfToken();

  return useCallback<VideoPlaybackResolver>(
    async (videoId) => {
      if (!videoId) return null;

      try {
        const csrfToken = await ensureCsrfToken();
        const response = await fetch('/video_playback', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
          },
          body: JSON.stringify({ videoId }),
        });
        if (response.status === 409) return VIDEO_PLAYBACK_PROCESSING;
        if (!response.ok) return null;
        return (await response.json()) as VideoPlaybackAuthorization;
      } catch {
        return null;
      }
    },
    [ensureCsrfToken],
  );
};

export default useVideoPlayback;
